import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CustomEncoder, ENVIRONMENT } from '@iot-platform/core';
import { ApiHelpers } from '@iot-platform/iot-platform-utils';
import {
  CommonApiListResponse,
  CommonApiRequest,
  CommonApiResponse,
  CommonCRUDService,
  CommonIndexedPagination,
  Environment
} from '@iot-platform/models/common';
import { Receiver, Site, Ward, WardBuilding, WardFloor } from '@iot-platform/models/oyan';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class WardsService implements CommonCRUDService<Ward, CommonIndexedPagination> {
  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly httpClient: HttpClient = inject(HttpClient);

  getAll(request?: CommonApiRequest): Observable<CommonApiResponse<Ward, CommonIndexedPagination>> {
    return this.httpClient
      .get(`${this.environment.api.url}${this.environment.api.endpoints.wards}`, { params: ApiHelpers.getHttpParams(request) })
      .pipe(map((response: CommonApiListResponse<Ward>) => ApiHelpers.getHttpResponse<Ward>(response)));
  }

  getById(id: string): Observable<Ward> {
    return this.httpClient.get<Ward>(`${this.environment.api.url}${this.environment.api.endpoints.wards}/${id}`);
  }

  addOne(ward: Ward): Observable<Ward> {
    return this.httpClient.post<Ward>(`${this.environment.api.url}${this.environment.api.endpoints.wards}`, ward);
  }

  updateOne(ward: Ward): Observable<Ward> {
    const body = {
      buildingId: ward.building?.id ?? null,
      comment: ward.comment ?? '',
      floorId: ward.floor?.id ?? null,
      name: ward.name,
      uf: ward.uf ?? ''
    };
    return this.httpClient.put<Ward>(`${this.environment.api.url}${this.environment.api.endpoints.wards}/${ward.id}`, body);
  }

  deleteOne(ward: Ward): Observable<Ward> {
    return this.httpClient.delete<Ward>(`${this.environment.api.url}${this.environment.api.endpoints.wards}/${ward.id}`);
  }

  getBuildings(sitePid: string): Observable<WardBuilding[]> {
    const params: HttpParams = new HttpParams({ encoder: new CustomEncoder() }).set('site_pid', sitePid);
    return this.httpClient.get<WardBuilding[]>(`${this.environment.api.url}${this.environment.api.endpoints.buildings}`, { params });
  }

  getFloors(buildingPid: string): Observable<WardFloor[]> {
    const params: HttpParams = new HttpParams({ encoder: new CustomEncoder() }).set('building_pid', buildingPid);
    return this.httpClient.get<WardFloor[]>(`${this.environment.api.url}${this.environment.api.endpoints.floors}`, { params });
  }

  deleteWard(ward: Ward): Observable<void> {
    return this.httpClient.delete<void>(`${this.environment.api.url}${this.environment.api.endpoints.wards}/${ward.id}`);
  }

  addReceiverToWard(receiver: Receiver, ward: Ward, site: Site): Observable<Receiver> {
    const params: HttpParams = new HttpParams({ encoder: new CustomEncoder() }).set('entityId', site.entity.id);
    return this.httpClient.post<Receiver>(
      `${this.environment.api.url}${this.environment.api.endpoints.receivers}/${receiver.id}/ward/${ward.id}`,
      {},
      { params }
    );
  }

  removeReceiverFromWard(receiver: Receiver, ward: Ward, site: Site): Observable<Receiver> {
    const params: HttpParams = new HttpParams({ encoder: new CustomEncoder() }).set('entityId', site.entity.id);
    return this.httpClient.delete<Receiver>(`${this.environment.api.url}${this.environment.api.endpoints.receivers}/${receiver.id}/ward/${ward.id}`, {
      params
    });
  }

  synchronize(site: Site): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `${this.environment.api.url}${this.environment.api.endpoints.sites}/${site.id}${this.environment.api.endpoints.receivers}/synchronize`
    );
  }
}
